import ElakayImage from '../../../components/images/ElakayImage';
// "Recently Viewed Providers" card -- the "Shops You Follow" card (pages/lakayUser/followPageMatrice.jsx)
// fed with PROVIDER data: same DOM structure and the same followPageMatrice.css classes (background
// from the last product image + teal gradient, name on top, "View Shop" link, heart top-right, 2x2
// mosaic of up to 4 products, pastel "empty box" placeholders), so both cards read as one family.
// Differences, on purpose:
//   - the heart is the Provider Like (never the Seller "follow shop" system);
//   - every product cell is a link to that Provider Product's page (/seller/provider-products/:id),
//     where the real Provider Product View is recorded -- the mosaic itself never records a view;
//   - the name / empty area opens the Provider Shop (/provider-shop/:id); the heart never navigates.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart, FaStar } from "react-icons/fa";
import { toImageUrl } from "../../../config";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import elakayEmptyBox from "../../Layout/icon/elakayEmptyBox.png";
import { FOLLOW_PLACEHOLDER_COLORS } from "../../pages/lakayUser/followPageMatrice";
import { providerStorefrontPath } from "../providerWorkspace";
import { providerShopDisplayName } from "../utils/providerShopIdentity";
import "../../pages/lakayUser/followPageMatrice.css";
import "./providerShowcaseCard.css";

const CELLS = 4;
// Same teal as the "Shops You Follow" section (pagesConfig followShopUser.content_BackGroundColor).
const SHOP_CARD_HSL = "195, 100%, 30%";

// Only the soft tones of the shared "Shops You Follow" palette (no Coral / Cyan / Chartreuse), so an
// incomplete mosaic stays calm on the teal card.
const SOFT = ["GhostWhite", "Ivory", "MintCream", "AliceBlue", "Azure", "HoneyDew", "LightBlue", "PowderBlue", "Lavender", "FloralWhite", "LightYellow", "Cornsilk"];
const PALETTE = FOLLOW_PLACEHOLDER_COLORS.filter((c) => SOFT.includes(c));

// Deterministic pastel per Provider + cell (the reference picks a random one on every render).
const placeholderColor = (seed, index) => {
  const s = `${seed}:${index}`;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const Placeholder = ({ seed, index }) => (
  <div className="product-square-card empty" data-rank="0">
    <div className="square-card-inner fallback-image-wrapper" style={{ backgroundColor: placeholderColor(seed, index) }}>
      <ElakayImage preset="product-card" className="fallback-image" src={elakayEmptyBox} alt="" aria-hidden="true" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    </div>
  </div>
);

function ProductCell({ product, index, seed }) {
  const [broken, setBroken] = React.useState(false);
  if (!product.image || broken) {
    // No usable image: keep the cell clickable, with the placeholder look.
    return (
      <Link to={`/seller/provider-products/${product._id}`} className="product-square-card empty provsc__cell" data-rank={index + 1} aria-label={product.name} title={product.name}>
        <div className="square-card-inner fallback-image-wrapper" style={{ backgroundColor: placeholderColor(seed, index) }}>
          <ElakayImage preset="product-card" className="fallback-image" src={elakayEmptyBox} alt="" aria-hidden="true" />
        </div>
      </Link>
    );
  }
  return (
    <Link to={`/seller/provider-products/${product._id}`} className="product-square-card provsc__cell" data-rank={index + 1} title={product.name}>
      <div className="square-card-inner">
        <ElakayImage preset="product-card" src={toImageUrl(product.image)} alt={product.name || text("h_provsc_product", "Product {value0}", { value0: index + 1 })} className="square-card-image" loading="lazy" onError={() => setBroken(true)} />
      </div>
    </Link>
  );
}

export default function ProviderShowcaseCard({ entry, onToggleLike, liking = false }) {
  const navigate = useNavigate();
  const id = String(entry.providerId || entry.provider?._id || "");
  const name = providerShopDisplayName(entry.shop, entry.provider) || text("h_ppc_provider", "Provider");
  const shopUrl = providerStorefrontPath(entry.provider?._id || id);
  const products = (entry.previewProducts || []).slice(0, CELLS);
  const cells = [...products, ...Array.from({ length: CELLS - products.length }, () => null)];
  const liked = Boolean(entry.isLikedByCurrentSeller);
  const caps = entry.capabilities || {};
  // Background: the last product's image, exactly like "Shops You Follow"; else the Provider logo;
  // else only the teal gradient (clean Elakay fallback).
  const lastImage = [...products].reverse().find((p) => p.image)?.image;
  const background = lastImage || entry.shop?.avatar || entry.provider?.avatar || "";
  // The card's empty areas open the Provider Shop; links and the heart keep their own behaviour.
  const onCardClick = (e) => { if (!e.target.closest("a, button")) navigate(shopUrl); };

  return (
    <div className="image-matrix-card provsc" data-provider-id={id}>
      {background && (
        <div className="card-background">
          <ElakayImage src={toImageUrl(background)} preset="product-card" alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          <div className="card-background-overlay" />
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard users have the name / View Shop links */}
      <div className="card-content_" onClick={onCardClick}
        style={{
          "--bg-color-1": `hsla(${SHOP_CARD_HSL}, 0.85)`, "--bg-color-2": `hsla(${SHOP_CARD_HSL}, 0.78)`, "--bg-color-3": `hsla(${SHOP_CARD_HSL}, 0.76)`,
          "--bg-color-4": `hsla(${SHOP_CARD_HSL}, 0.75)`, "--bg-color-5": `hsla(${SHOP_CARD_HSL}, 0.73)`, "--bg-color-6": `hsla(${SHOP_CARD_HSL}, 0.60)`,
        }}>
        <div className="card-info-zone">
          <div className="info-zone-content">
            <h3 className="card-title provsc__title">
              <Link to={shopUrl} className="provsc__name" title={name}>{name}</Link>
              {onToggleLike && (
                <button type="button" className={`provsc__like${liked ? " provsc__like--on" : ""}`} aria-pressed={liked} disabled={liking}
                  aria-label={liked ? text("h_provx_unlike", "Unlike {value0}", { value0: name }) : text("h_provx_like", "Like {value0}", { value0: name })}
                  onClick={(e) => { e.stopPropagation(); onToggleLike(entry); }}>
                  {liked ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
                </button>
              )}
            </h3>
            <div className="provsc__sub">
              <Link className="card-subtitle" to={shopUrl}>{text("h_provsc_view_shop", "View Shop")}</Link>
              {/* Compact secondary facts only (the mosaic stays the main element). */}
              {Number.isFinite(entry.rating) && <span className="provsc__chip"><FaStar aria-hidden="true" /> {entry.rating.toFixed(1)}</span>}
              {caps.wholesale && <span className="provsc__chip">{text("h_provc_wholesale", "Wholesale")}</span>}
              {caps.dropshipping && <span className="provsc__chip">{text("h_provc_dropshipping", "Dropshipping")}</span>}
            </div>
          </div>
        </div>
        <div className="card-images-zone">
          <div className="products-matrix matrix-4">
            {cells.map((p, index) => (p
              ? <ProductCell key={String(p._id)} product={p} index={index} seed={id} />
              : <Placeholder key={`empty-${index}`} seed={id} index={index} />))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Same silhouette: name + link lines, heart circle, 2x2 squares.
export const ProviderShowcaseSkeleton = () => (
  <div className="image-matrix-card provsc provsc--skeleton" aria-hidden="true">
    <div className="card-content_">
      <div className="info-zone-content">
        <div className="provsc__title"><span className="sph-sk provsc__sk-name" /><span className="sph-sk provsc__sk-heart" /></div>
        <span className="sph-sk provsc__sk-link" />
      </div>
      <div className="card-images-zone">
        <div className="products-matrix matrix-4">{Array.from({ length: CELLS }, (_, i) => <div key={i} className="product-square-card sph-sk" />)}</div>
      </div>
    </div>
  </div>
);
